import { useState } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { ManagerialDashboardFinance } from '@/types/api'
import { formatFinancialMonth } from '../../lib/executive-finance'
import {
  formatCompactCurrencyBrl,
  formatFullCurrencyBrl,
} from '../../lib/managerial-kpis'
import styles from './ExecutiveFinance.module.css'

export function RevenueExpensesChart({
  finance,
  isLoading,
}: {
  finance: ManagerialDashboardFinance | undefined
  isLoading: boolean
}) {
  const [months, setMonths] = useState(12)
  const points = finance?.points.slice(-months) ?? []
  const revenue =
    points.reduce((total, point) => total + Math.round(point.revenue * 100), 0) / 100
  const expenses =
    points.reduce((total, point) => total + Math.round(point.expenses * 100), 0) / 100
  return (
    <section
      className={styles.chartCard}
      aria-labelledby="platform-finance-title"
      aria-busy={isLoading}
    >
      <div className={styles.chartHeading}>
        <div>
          <h2 id="platform-finance-title">Receita da plataforma e despesas</h2>
          <p>
            Consolidado mensal da plataforma · independente dos filtros do topo · mês
            atual parcial
          </p>
        </div>
        <div className={styles.periods} role="group" aria-label="Período financeiro">
          {[6, 12].map((value) => (
            <button
              type="button"
              key={value}
              aria-pressed={months === value}
              onClick={() => setMonths(value)}
            >
              {value} meses
            </button>
          ))}
        </div>
      </div>
      <dl className={styles.totals}>
        <div>
          <dt>Receita da plataforma</dt>
          <dd>{finance ? formatFullCurrencyBrl(revenue) : '—'}</dd>
        </div>
        <div>
          <dt>Despesas registradas</dt>
          <dd>{finance ? formatFullCurrencyBrl(expenses) : '—'}</dd>
        </div>
        <div>
          <dt>Saldo dos registros</dt>
          <dd>{finance ? formatFullCurrencyBrl(revenue - expenses) : '—'}</dd>
        </div>
      </dl>
      <ul className={styles.legend} aria-label="Legenda financeira">
        <li>
          <span className={styles.revenueDot} />
          Receita da plataforma
        </li>
        <li>
          <span className={styles.expenseDot} />
          Despesas
        </li>
      </ul>
      <div className={styles.chart}>
        {!finance ? (
          <p>
            {isLoading
              ? 'Carregando dados financeiros...'
              : 'Dados financeiros ainda indisponíveis.'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={points}
              margin={{ top: 12, right: 16, left: 8, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="var(--color-border-subtle)"
              />
              <XAxis
                dataKey="month"
                tickFormatter={formatFinancialMonth}
                tickLine={false}
                axisLine={false}
                fontSize={11}
              />
              <YAxis
                tickFormatter={formatCompactCurrencyBrl}
                tickLine={false}
                axisLine={false}
                width={90}
                fontSize={11}
              />
              <Tooltip
                labelFormatter={(value) => formatFinancialMonth(String(value))}
                formatter={(value, name) => [
                  formatFullCurrencyBrl(Number(value)),
                  String(name),
                ]}
              />
              <Bar
                dataKey="expenses"
                name="Despesas"
                fill="#d97706"
                radius={[4, 4, 0, 0]}
                maxBarSize={32}
              />
              <Line
                dataKey="revenue"
                name="Receita da plataforma"
                type="monotone"
                stroke="#059669"
                strokeWidth={3}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      <p className={styles.scopeNote}>
        Receita: parcela da Legal Prime nos splits registrados. Despesas: custos com
        competência, AWS, marketing, WhatsApp e taxas de pagamento. Repasses de terceiros
        ficam fora para evitar dupla dedução. PIX segue as tarifas atuais.
      </p>
      {finance && finance.undated_expenses > 0 ? (
        <p className={styles.coverageNote}>
          {formatFullCurrencyBrl(finance.undated_expenses)} em custos sem competência não
          entram no gráfico. Defina o mês nos custos operacionais.
        </p>
      ) : null}
      {finance && finance.unconverted_percentage_entries > 0 ? (
        <p className={styles.coverageNote}>
          Há percentuais sem valor monetário definido, ainda fora das despesas mensais.
        </p>
      ) : null}
      <p className={styles.scopeNote}>
        O saldo dos registros não representa EBITDA nem fluxo de caixa. Indicadores sem
        base são exibidos com “—”.
      </p>
      <details className={styles.accessibleData}>
        <summary>Ver valores mensais em tabela</summary>
        <table>
          <thead>
            <tr>
              <th>Mês</th>
              <th>Receita da plataforma</th>
              <th>Despesas</th>
            </tr>
          </thead>
          <tbody>
            {points.map((point) => (
              <tr key={point.month}>
                <th>{formatFinancialMonth(point.month)}</th>
                <td>{formatFullCurrencyBrl(point.revenue)}</td>
                <td>{formatFullCurrencyBrl(point.expenses)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </section>
  )
}
